import { useEffect, useState } from "react";
import { ArrowRight, Check, HelpCircle, AlertTriangle, Info, Loader2, Package, Ban } from "lucide-react";
import { toast } from "sonner";
import AnalysisProgress from "@/components/AnalysisProgress";
import { eur } from "@/lib/format";
import api from "@/lib/api";

const MODES = [
  { v: "rama_supplies_all", l: "RAMA DÉCO fournit les matériaux" },
  { v: "client_supplies_materials", l: "Je fournis les matériaux" },
  { v: "unknown", l: "Je ne sais pas encore" },
];

const LEVELS = {
  detailed: "Estimation détaillée",
  refined: "Estimation affinée",
  preliminary: "Estimation préliminaire",
  insufficient: "Étude nécessaire",
};

const Card = ({ title, kicker, children, testid }) => (
  <div data-testid={testid} className="bg-white border border-black/10 rounded-rama-md p-6 md:p-8">
    {kicker && <div className="font-mono-caption text-bordeaux-700 mb-2">{kicker}</div>}
    {title && <div className="font-display text-2xl text-anthracite mb-5">{title}</div>}
    {children}
  </div>
);

export default function StudyPanel({ project, onVisit }) {
  const [study, setStudy] = useState(null);
  const [busy, setBusy] = useState(false);
  const [quick, setQuick] = useState(null);
  const [surface, setSurface] = useState(project.surface || "");
  const [deep, setDeep] = useState(false);
  const [mode, setMode] = useState("unknown");
  const [qty, setQty] = useState({});
  const [answers, setAnswers] = useState({});
  const [linked, setLinked] = useState({});

  const [qbusy, setQbusy] = useState(false);
  const [progress, setProgress] = useState(null);

  const runQuick = async (s = surface, m = mode, notify = false) => {
    setQbusy(true);
    try {
      const { data } = await api.post("/study/quick", {
        description: project.description, project_id: project.id,
        surface_m2: s ? parseFloat(s) : null, materials_mode: m,
      });
      setQuick(data);
      if (data.surface_m2 && !s) setSurface(data.surface_m2);
      if (notify) toast.success("Estimation recalculée");
    } catch { toast.error("Estimation rapide indisponible"); }
    finally { setQbusy(false); }
  };

  const pollDeep = async (id) => {
    for (let i = 0; i < 150; i++) {
      await new Promise((r) => setTimeout(r, 1200));
      try {
        const { data } = await api.get(`/study/${id}`);
        if (data.progress) setProgress(data.progress);
        if (data.status !== "analyzing") {
          setProgress({ pct: 100, step: 4, label: "Estimation prête" });
          setStudy(data);
          setMode(data.answers?.materials_mode || data.understanding?.materials_mode || mode);
          setTimeout(() => setDeep(false), 500);
          if (data.status === "error") toast.error("Étude approfondie indisponible, réessayez");
          else toast.success("Étude approfondie terminée");
          return;
        }
      } catch { /* retry */ }
    }
    setDeep(false);
  };

  const startDeep = async () => {
    setDeep(true);
    setProgress({ pct: 5, step: 0, label: "Préparation de votre dossier" });
    try {
      const { data } = await api.post("/study/analyze", {
        description: project.description, project_id: project.id,
        answers: { materials_mode: mode, surface_m2: surface ? parseFloat(surface) : null },
      });
      pollDeep(data.id);
    } catch {
      setDeep(false);
      toast.error("Analyse indisponible pour le moment");
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      runQuick();
      try {
        const { data } = await api.get(`/study/by-project/${project.id}`);
        if (!alive) return;
        if (data.status === "ready") {
          setStudy(data);
          setMode(data.answers?.materials_mode || data.understanding?.materials_mode || "unknown");
        } else if (data.status === "analyzing") {
          setDeep(true);
          pollDeep(data.id);
        }
      } catch { /* pas d'étude approfondie : estimation rapide seule */ }
    })();
    return () => { alive = false; };
  }, [project.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (extra = {}) => {
    if (!study) { await runQuick(surface, extra.mode || mode); return; }
    setBusy(true);
    try {
      const services = [
        ...Object.entries(qty).filter(([, v]) => v !== "" && v != null)
          .map(([code, v]) => ({ service_code: code, quantity: parseFloat(v), included: true })),
        ...Object.entries(linked).filter(([, v]) => v).map(([code]) => ({
          service_code: code, included: true,
          ...(qty[code] ? { quantity: parseFloat(qty[code]) } : {}),
        })),
      ];
      const { data } = await api.put(`/study/${study.id}`, {
        answers: { ...answers, ...(extra.answers || {}), surface_m2: surface ? parseFloat(surface) : null },
        materials_mode: extra.mode || mode,
        services,
        recompute: true,
      });
      setStudy(data);
      toast.success("Estimation mise à jour");
      setTimeout(() => document.getElementById("study-result")?.scrollIntoView({ behavior: "smooth" }), 150);
    } catch {
      toast.error("Erreur lors du calcul");
    } finally {
      setBusy(false);
    }
  };

  const qp = quick?.pricing;
  const ready = study && study.status === "ready" && study.understanding;
  const askSurface = quick?.needsSurface && !surface;

  const SurfaceField = (
    <div className="flex flex-wrap items-end gap-4">
      <label className="text-sm text-ink-soft">
        Surface au sol concernée (m²)
        <input type="number" inputMode="decimal" data-testid="quick-surface" min="1"
          value={surface} onChange={(e) => setSurface(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") runQuick(e.target.value, mode, true); }}
          className="block w-32 mt-1 border-b border-black/25 py-2 bg-transparent focus:border-bordeaux-700 outline-none text-anthracite" />
      </label>
      <button data-testid="quick-recompute" onClick={() => runQuick(surface, mode, true)} disabled={qbusy}
        className="btn-primary disabled:opacity-50">
        {qbusy ? "Calcul…" : askSurface ? "Voir mon estimation" : "Recalculer"} {!qbusy && <ArrowRight size={15} />}
      </button>
    </div>
  );

  const NextStepVisit = onVisit ? (
    <div data-testid="next-step-visit" className="bg-bordeaux-light border-l-2 border-bordeaux-700 rounded-r-rama-md p-6 md:p-8">
      <div className="font-mono-caption text-bordeaux-700 mb-2">Prochaine étape</div>
      <div className="font-display text-2xl text-anthracite">La visite technique, c'est l'étape suivante.</div>
      <p className="mt-3 text-sm text-ink-soft leading-relaxed max-w-2xl">
        Aucune estimation en ligne ne remplace la prise de mesures sur place : état des supports, contraintes d'accès,
        réseaux existants. La visite est gratuite, sans engagement, et débouche sur un <b>devis définitif</b> poste par
        poste. Vous n'avez rien à préparer, nous relevons tout.
      </p>
      <button data-testid="next-step-visit-btn" onClick={onVisit} className="btn-primary mt-6">
        Planifier ma visite technique <ArrowRight size={16} />
      </button>
    </div>
  ) : null;

  const QuickBlock = (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="quick-estimate">
      <div className="lg:col-span-8 space-y-6">
        <Card kicker="Estimation immédiate" title={askSurface ? "Une information et c'est parti" : "Votre budget indicatif"}>
          {askSurface ? (
            <>
              <p className="text-sm text-ink-soft leading-relaxed mb-5">
                Indiquez la surface au sol de la pièce ou du logement concerné : c'est la donnée qui fait varier le
                budget. Nous en déduisons les métrés (murs, plafonds, sols), que vous pourrez corriger ensuite.
              </p>
              {SurfaceField}
            </>
          ) : qp ? (
            <>
              <div className="font-display text-3xl md:text-4xl text-anthracite" data-testid="quick-range">
                {eur(qp.totalPricedLowHT)} – {eur(qp.totalPricedHighHT)} <span className="text-lg">HT</span>
              </div>
              {qp.showTTC && (
                <div className="text-sm text-ink-soft mt-1">
                  soit {eur(qp.totalPricedLowTTC)} – {eur(qp.totalPricedHighTTC)} TTC (TVA {qp.vatRate} %)
                </div>
              )}
              <div className="mt-5">{SurfaceField}</div>
              <ul className="mt-5 space-y-1.5">
                {(quick.proxies || []).map((p, i) => (
                  <li key={i} className="text-xs text-ink-soft flex items-start gap-1.5">
                    <Info size={12} className="mt-0.5 shrink-0" /> {p}
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-sm text-ink-soft leading-relaxed">{quick.message}</p>
            </>
          ) : (
            <div className="flex items-center gap-3 text-ink-soft text-sm">
              <Loader2 className="animate-spin" size={16} /> Calcul de votre estimation immédiate…
            </div>
          )}
        </Card>

        {!askSurface && qp?.lines?.length > 0 && (
          <Card kicker="Détail indicatif" title="Ce que couvre ce budget" testid="quick-lines">
            <ul className="divide-y divide-black/10">
              {qp.lines.map((l) => (
                <li key={l.serviceCode} data-testid={`quick-line-${l.serviceCode}`}
                  className="py-3 flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-anthracite text-sm">
                    {l.label}
                    {l.quantity ? <span className="text-ink-soft"> · {l.quantity} {l.unit}</span> : null}
                  </span>
                  {l.lowHT != null ? (
                    <span className="font-mono-caption text-anthracite text-sm">{eur(l.lowHT)} – {eur(l.highHT)} HT</span>
                  ) : (
                    <span className="font-mono-caption text-bordeaux-700 text-sm">À confirmer</span>
                  )}
                </li>
              ))}
            </ul>
            {qp.unpricedServices?.length > 0 && (
              <p className="mt-4 text-xs text-ink-soft">
                Postes encore à étudier, non compris ci-dessus : {qp.unpricedServices.map((x) => x.label).join(" · ")}.
              </p>
            )}
          </Card>
        )}

        {!ready && !askSurface && (
          <Card kicker="Aller plus loin" title="Étude approfondie de votre chantier" testid="deep-cta">
            <p className="text-sm text-ink-soft leading-relaxed">
              Notre assistant de pré-étude relit votre demande en détail : prestations liées souvent oubliées, points
              de vigilance, quantités à confirmer et questions utiles. Comptez environ une minute.
            </p>
            <p className="mt-3 text-sm text-ink-soft leading-relaxed">
              Vous n'avez pas les réponses (métrés, état des supports, références de matériaux) ? C'est normal, et ce
              n'est pas à vous de les chercher : demandez simplement la visite technique, nous prenons les mesures
              sur place et vous délivrons directement un devis définitif.
            </p>
            {deep ? (
              <div className="mt-6"><AnalysisProgress active progress={progress} /></div>
            ) : (
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button data-testid="start-deep" onClick={startDeep} className="btn-primary">
                  Lancer l'étude approfondie <ArrowRight size={16} />
                </button>
                {onVisit && (
                  <button data-testid="quick-visit" onClick={onVisit} className="btn-ghost">
                    Visite technique gratuite
                  </button>
                )}
              </div>
            )}
          </Card>
        )}

        {!askSurface && NextStepVisit}
      </div>

      <div className="lg:col-span-4">
        <div className="bg-anthracite text-ivory p-6 md:p-8 rounded-rama-md">
          <div className="font-mono-caption text-greige mb-3">Mesures et devis</div>
          <div className="font-display text-2xl leading-tight">Une visite technique gratuite, sans engagement.</div>
          <p className="mt-3 text-sm text-white/70">
            Nous prenons les mesures exactes, vérifions l'état des supports et établissons un devis détaillé poste par
            poste. Gratuit, y compris si vous ne donnez pas suite.
          </p>
          {onVisit && (
            <button data-testid="quick-visit-side" onClick={onVisit} className="btn-primary w-full justify-center mt-5">
              Planifier ma visite <ArrowRight size={16} />
            </button>
          )}
          <div className="mt-5 pt-5 border-t border-white/15 text-xs text-white/60 leading-relaxed">
            {qp?.disclaimer || "Estimation indicative, non contractuelle."}
          </div>
        </div>
      </div>
    </div>
  );

  if (!ready) {
    return <div className="mt-12 space-y-8" data-testid="study-panel">{QuickBlock}</div>;
  }

  const u = study.understanding || {};
  const p = study.pricing;
  const services = (u.detected_services || []).filter((s) => s.included !== false);
  const suggested = (u.detected_services || []).filter((s) => s.included === false);
  const linkedItems = [
    ...suggested.map((s) => ({ service_code: s.service_code, client_label: s.client_label,
      reason: s.source_evidence?.[0] || "Détectée dans votre description, à confirmer",
      unit: s.unit, quantity: s.quantity })),
    ...(u.linked_services_to_confirm || []).map((l) => ({ ...l, unit: null, quantity: null })),
  ];
  const toConfirm = services.filter((s) => s.requires_confirmation || !s.quantity);
  const included = services.filter((s) => !s.requires_confirmation && s.quantity);
  const questions = (u.required_questions || []).slice(0, 5);

  return (
    <div className="mt-12 space-y-8" data-testid="study-panel">
      {/* PRESTATIONS */}
      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-8">
          <Card kicker="Ce que nous avons compris" title="Prestations identifiées" testid="study-services">
            {included.length === 0 && toConfirm.length === 0 && (
              <p className="text-ink-soft text-sm">Aucune prestation n'a encore pu être identifiée avec certitude.</p>
            )}
            <ul className="space-y-3">
              {included.map((s) => (
                <li key={s.service_code} data-testid={`study-service-${s.service_code}`} className="flex items-start gap-3">
                  <Check size={18} className="text-bordeaux-700 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-anthracite font-medium">{s.client_label}</div>
                    <div className="text-xs text-ink-soft">
                      {s.quantity} {s.unit} · {s.internal_scope?.slice(0, 3).join(" · ")}
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            {toConfirm.length > 0 && (
              <div className="mt-6 pt-6 border-t border-black/10">
                <div className="font-mono-caption text-ink-soft mb-4">À confirmer — quantités manquantes</div>
                <ul className="space-y-4">
                  {toConfirm.map((s) => (
                    <li key={s.service_code} className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="text-anthracite">{s.client_label}</div>
                        <div className="text-xs text-ink-soft">Unité : {s.unit}</div>
                      </div>
                      <input
                        type="number" inputMode="decimal"
                        data-testid={`study-qty-${s.service_code}`}
                        value={qty[s.service_code] ?? (s.quantity || "")}
                        onChange={(e) => setQty({ ...qty, [s.service_code]: e.target.value })}
                        placeholder={s.unit}
                        className="w-28 border-b border-black/25 py-2 bg-transparent focus:border-bordeaux-700 outline-none"
                      />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>

          {/* FOURNITURES */}
          <Card kicker="Fournitures" title="Qui fournit les matériaux ?" testid="study-materials">
            <div className="flex flex-wrap gap-2">
              {MODES.map((m) => (
                <button key={m.v} data-testid={`materials-${m.v}`} onClick={() => setMode(m.v)}
                  className={`px-4 py-2.5 rounded-full border text-sm transition-colors ${
                    mode === m.v ? "bg-anthracite text-ivory border-anthracite" : "border-black/20 hover:border-anthracite"}`}>
                  {m.l}
                </button>
              ))}
            </div>
            {mode === "client_supplies_materials" && (
              <p className="mt-4 text-sm text-ink-soft leading-relaxed">
                Nous vous demanderons quels produits exactement vous fournissez (peinture, enduit, impression,
                abrasifs, protections) : aucune remise arbitraire n'est appliquée, seules les fournitures réellement
                identifiées sont retirées du prix.
              </p>
            )}
          </Card>

          {/* PRESTATIONS LIÉES / PROPOSÉES */}
          {linkedItems.length > 0 && (
            <Card kicker="Proposées — à confirmer" title="Prestations complémentaires" testid="study-linked">
              <ul className="space-y-3">
                {linkedItems.map((l) => (
                  <li key={l.service_code} className="flex items-start gap-3">
                    <input type="checkbox" data-testid={`linked-${l.service_code}`}
                      checked={!!linked[l.service_code]}
                      onChange={(e) => setLinked({ ...linked, [l.service_code]: e.target.checked })}
                      className="mt-1 accent-[#6B0E1A]" />
                    <div className="flex-1">
                      <div className="text-anthracite">{l.client_label}</div>
                      <div className="text-xs text-ink-soft">{l.reason}</div>
                    </div>
                    {linked[l.service_code] && (
                      <input type="number" inputMode="decimal"
                        data-testid={`study-qty-${l.service_code}`}
                        value={qty[l.service_code] ?? (l.quantity || "")}
                        onChange={(e) => setQty({ ...qty, [l.service_code]: e.target.value })}
                        placeholder={l.unit || "qté"}
                        className="w-24 border-b border-black/25 py-1 bg-transparent focus:border-bordeaux-700 outline-none text-sm" />
                    )}
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {/* QUESTIONS ADAPTATIVES */}
          {questions.length > 0 && (
            <Card kicker="2 à 5 questions utiles" title="Quelques précisions" testid="study-questions">
              <div className="mb-6 p-4 bg-ivory border-l-2 border-bordeaux-700 rounded-r-rama-sm">
                <p className="text-sm text-ink-soft leading-relaxed">
                  <b className="text-anthracite">Vous n'avez pas les réponses ?</b> Laissez tomber les questions et
                  faites simplement appel à nous : nous venons prendre les mesures et relever l'état des supports,
                  puis nous vous délivrons directement un devis définitif.
                </p>
                {onVisit && (
                  <button data-testid="questions-visit" onClick={onVisit} className="btn-ghost mt-4 text-sm">
                    Demander la visite technique <ArrowRight size={14} />
                  </button>
                )}
              </div>
              <div className="space-y-6">
                {questions.map((q) => (
                  <div key={q.id}>
                    <div className="text-anthracite font-medium">{q.question}</div>
                    <div className="text-xs text-ink-soft mt-1 flex items-start gap-1.5">
                      <HelpCircle size={13} className="mt-0.5 shrink-0" /> {q.reason}
                    </div>
                    {q.type === "single_choice" && (q.options || []).length > 0 ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {q.options.map((o) => (
                          <button key={o} data-testid={`answer-${q.id}-${o}`}
                            onClick={() => setAnswers({ ...answers, [q.id]: o })}
                            className={`px-3.5 py-2 rounded-full border text-sm ${
                              answers[q.id] === o ? "bg-anthracite text-ivory border-anthracite" : "border-black/20 hover:border-anthracite"}`}>
                            {o}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <input
                        type={q.type === "number" ? "number" : "text"}
                        data-testid={`answer-${q.id}`}
                        value={answers[q.id] || ""}
                        onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                        className="mt-3 w-full max-w-sm border-b border-black/25 py-2 bg-transparent focus:border-bordeaux-700 outline-none"
                      />
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* RISQUES / HORS PÉRIMÈTRE */}
          {((u.risk_flags || []).length > 0 || (u.out_of_scope_items || []).length > 0) && (
            <Card kicker="Points de vigilance" title="À vérifier avant chiffrage" testid="study-risks">
              <ul className="space-y-3 text-sm">
                {(u.risk_flags || []).map((r, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <AlertTriangle size={16} className="text-bordeaux-700 mt-0.5 shrink-0" />
                    <span className="text-ink-soft"><b className="text-anthracite">{r.flag}</b> — {r.reason}</span>
                  </li>
                ))}
                {(u.out_of_scope_items || []).map((o, i) => (
                  <li key={`o${i}`} className="flex items-start gap-3">
                    <Ban size={16} className="text-anthracite mt-0.5 shrink-0" />
                    <span className="text-ink-soft">{o} — hors périmètre RAMA DÉCO (gros œuvre / structure)</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>

        {/* SIDEBAR */}
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-32 bg-anthracite text-ivory p-6 md:p-8 rounded-rama-md">
            <div className="font-mono-caption text-greige mb-3">Mon projet {project.ref}</div>
            <div className="font-display text-2xl leading-tight">{u.project_summary?.slice(0, 90) || "Pré-étude en cours"}</div>
            {p && p.totalPricedCentralHT > 0 ? (
              <div className="mt-6" data-testid="study-sidebar-total">
                <div className="font-mono-caption text-greige mb-2">{LEVELS[p.level] || "Estimation"}</div>
                <div className="font-display text-3xl">{eur(p.totalPricedCentralHT)} HT</div>
                <div className="text-xs text-white/60 mt-1">
                  Fourchette {eur(p.totalPricedLowHT)} – {eur(p.totalPricedHighHT)} HT (±{p.uncertaintyPct} %)
                </div>
                {p.showTTC && (
                  <div className="text-xs text-white/60 mt-1">
                    Soit {eur(p.totalPricedCentralTTC)} TTC (TVA {p.vatRate} %)
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-6 text-sm text-white/70">
                Complétez les quantités et le mode de fourniture pour obtenir une fourchette.
              </div>
            )}
            <button data-testid="study-compute" onClick={() => save()} disabled={busy}
              className="btn-primary w-full justify-center mt-6 disabled:opacity-50">
              {busy ? "Calcul…" : "Mettre à jour mon estimation"} {!busy && <ArrowRight size={16} />}
            </button>
            {onVisit && (
              <button data-testid="study-visit" onClick={onVisit} className="btn-ghost btn-ghost-light w-full justify-center mt-3">
                Planifier une visite technique
              </button>
            )}
            <div className="mt-6 pt-6 border-t border-white/15 text-xs text-white/60 leading-relaxed">
              {p?.disclaimer ||
                "Cette estimation est indicative et ne constitue pas un devis contractuel."}
            </div>
          </div>
        </div>
      </div>

      {/* RÉSULTAT */}
      {p && (
        <div id="study-result" className="space-y-8" data-testid="study-result">
          <Card kicker={LEVELS[p.level]} title="Détail de l'estimation">
            <ul className="divide-y divide-black/10">
              {p.lines.map((l) => (
                <li key={l.serviceCode} data-testid={`study-line-${l.serviceCode}`} className="py-4">
                  <div className="flex flex-wrap items-baseline justify-between gap-3">
                    <span className="text-anthracite">
                      {l.label}
                      {l.quantity ? <span className="text-ink-soft text-sm"> · {l.quantity} {l.unit}</span> : null}
                    </span>
                    {l.centralHT != null ? (
                      <span className="font-mono-caption text-anthracite">
                        {eur(l.lowHT)} – {eur(l.highHT)} HT
                      </span>
                    ) : (
                      <span className="font-mono-caption text-bordeaux-700">À confirmer</span>
                    )}
                  </div>
                  {l.assumptions?.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {l.assumptions.map((a, i) => (
                        <li key={i} className="text-xs text-ink-soft flex items-start gap-1.5">
                          <Info size={12} className="mt-0.5 shrink-0" /> {a}
                        </li>
                      ))}
                    </ul>
                  )}
                  {l.missingInputs?.length > 0 && (
                    <div className="mt-2 text-xs text-bordeaux-700">Manque : {l.missingInputs.join(" · ")}</div>
                  )}
                </li>
              ))}
            </ul>
          </Card>

          {p.unpricedServices?.length > 0 && (
            <Card kicker="Non chiffré à ce stade" title="Postes encore à étudier" testid="study-unpriced">
              <ul className="space-y-3 text-sm">
                {p.unpricedServices.map((x, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Package size={15} className="text-bordeaux-700 mt-0.5 shrink-0" />
                    <span className="text-ink-soft"><b className="text-anthracite">{x.label}</b> — {x.reason}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-ink-soft">
                Ces postes ne sont pas inclus dans le total ci-dessus : le montant affiché ne couvre donc pas
                l'intégralité du projet.
              </p>
            </Card>
          )}

          {(p.exclusions?.length > 0 || (u.explicit_exclusions || []).length > 0) && (
            <Card kicker="Exclusions" title="Ce qui n'est pas compris" testid="study-exclusions">
              <ul className="space-y-2 text-sm text-ink-soft">
                {(u.explicit_exclusions || []).map((e, i) => <li key={`e${i}`}>• {e}</li>)}
                {(p.exclusions || []).map((e, i) => <li key={`x${i}`}>• {e}</li>)}
              </ul>
            </Card>
          )}

          {p.nextBestQuestions?.length > 0 && (
            <Card kicker="Pour gagner en précision" title="Prochaines informations utiles" testid="study-next">
              <ul className="space-y-2 text-sm text-ink-soft">
                {p.nextBestQuestions.map((q, i) => <li key={i}>• {q}</li>)}
              </ul>
            </Card>
          )}

          {p.requiresVisit && (
            <div data-testid="study-visit-needed" className="p-6 bg-bordeaux-light border-l-2 border-bordeaux-700 rounded-r-rama-md">
              <div className="font-display text-xl text-anthracite">Une visite technique est recommandée</div>
              <p className="mt-2 text-sm text-ink-soft max-w-2xl">
                Les informations disponibles ne permettent pas encore un chiffrage ferme sur l'ensemble des postes.
                La visite est gratuite, sans engagement, et permet d'établir un devis détaillé poste par poste.
              </p>
              {onVisit && (
                <button onClick={onVisit} className="btn-primary mt-5">Planifier ma visite <ArrowRight size={16} /></button>
              )}
            </div>
          )}

          {!p.requiresVisit && NextStepVisit}
        </div>
      )}
    </div>
  );
}
